import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    useTheme,
    Alert,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    ToggleButtonGroup,
    ToggleButton,
    Slider,
    Grid,
} from '@mui/material';
import { DeleteOutline, Save, CloudUpload, Crop, Videocam, Image as ImageIcon, FitScreen, Fullscreen, CenterFocusStrong, ZoomIn, AspectRatio } from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import { Area, Point } from 'react-easy-crop/types';
import { BasicButton } from '../../../../../components/BasicButton';
import apiClient from '../../../../../services/api/ApiClient';
import { ApiEndpoints } from '../../../../../config/constants';
import { PermissionWrapper } from '../../../../../components/PermissionWrapper';
import { useStoreConfig } from '../../../../../contexts/StoreConfigContext';

// Helper function to create image from blob
const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.src = url;
    });

// Helper function to get cropped image
async function getCroppedImg(
    imageSrc: string,
    pixelCrop: Area
): Promise<Blob> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('No 2d context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Canvas is empty'));
            }
        }, 'image/webp', 0.95);
    });
}

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

export const HeaderImagePicker: React.FC = () => {
    const theme = useTheme();
    const { reloadConfig, config } = useStoreConfig();
    const [isSaving, setIsSaving] = useState(false);
    const [mediaUrl, setMediaUrl] = useState<string | null>(null);
    const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
    const [mediaMimeType, setMediaMimeType] = useState<string>('image/webp');
    const [mediaType, setMediaType] = useState<'image' | 'video'>('image');
    const [objectFit, setObjectFit] = useState<'cover' | 'contain' | 'fill'>('cover');
    const [hasChanges, setHasChanges] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Crop modal state (only for images)
    const [isCropModalOpen, setIsCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

    // Video position/scale state
    const [videoPosition, setVideoPosition] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
    const [videoScale, setVideoScale] = useState(1.0);

    // Load current settings
    useEffect(() => {
        if (config.appHeaderImage) {
            setMediaUrl(config.appHeaderImage);
            setMediaType(config.appHeaderMediaType || 'image');
        }
        setObjectFit(config.appHeaderObjectFit || 'cover');
        setVideoPosition(config.appHeaderPosition || { x: 50, y: 50 });
        setVideoScale(config.appHeaderScale || 1.0);
    }, [config.appHeaderImage, config.appHeaderMediaType, config.appHeaderObjectFit, config.appHeaderPosition, config.appHeaderScale]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
        const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type);

        // Validate file type
        if (!isImage && !isVideo) {
            setError('Please select an image (PNG, JPG, WEBP) or video (MP4, WebM) file');
            return;
        }

        // Validate file size - 5MB for images, 3MB for videos
        const maxSize = isVideo ? 3 * 1024 * 1024 : 5 * 1024 * 1024;
        const maxSizeLabel = isVideo ? '3MB' : '5MB';
        
        if (file.size > maxSize) {
            setError(`File size must be less than ${maxSizeLabel}`);
            return;
        }

        if (isVideo) {
            // For videos, skip cropping - directly set the file
            const videoUrl = URL.createObjectURL(file);
            setMediaUrl(videoUrl);
            setMediaBlob(file);
            setMediaMimeType(file.type);
            setMediaType('video');
            setHasChanges(true);
            setError(null);
        } else {
            // For images, show crop modal
            const reader = new FileReader();
            reader.onload = () => {
                setImageToCrop(reader.result as string);
                setIsCropModalOpen(true);
                setError(null);
            };
            reader.readAsDataURL(file);
        }
        
        // Reset input value so same file can be selected again
        event.target.value = '';
    };

    const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropConfirm = async () => {
        if (!imageToCrop || !croppedAreaPixels) return;

        try {
            const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);
            const croppedUrl = URL.createObjectURL(croppedBlob);
            
            setMediaUrl(croppedUrl);
            setMediaBlob(croppedBlob);
            setMediaType('image');
            setHasChanges(true);
            setIsCropModalOpen(false);
            setImageToCrop(null);
            setSuccess(null);
        } catch (err) {
            setError('Failed to crop image');
            console.error(err);
        }
    };

    const handleCropCancel = () => {
        setIsCropModalOpen(false);
        setImageToCrop(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const handleSave = async () => {
        if (!mediaBlob) {
            setError('No file to upload');
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const formData = new FormData();
            // Get extension from MIME type
            const mimeToExt: Record<string, string> = {
                'video/mp4': 'mp4',
                'video/webm': 'webm',
                'video/quicktime': 'mov',
                'image/webp': 'webp',
                'image/png': 'png',
                'image/jpeg': 'jpg',
                'image/jpg': 'jpg',
            };
            const extension = mimeToExt[mediaMimeType] || (mediaType === 'video' ? 'mp4' : 'webp');
            formData.append('file', mediaBlob, `header.${extension}`);

            const response = await apiClient.post<{ success: boolean; url: string; mediaType: 'image' | 'video' }>(
                ApiEndpoints.AppHeader.UploadHeader,
                formData
            );

            // Update position and scale to defaults after uploading new media
            await apiClient.patch(
                ApiEndpoints.AppHeader.UpdateHeaderPosition,
                {
                    position: { x: 50, y: 50 },
                    scale: 1.0,
                }
            );

            // Use the URL from the response with cache busting for immediate preview
            const uploadedUrl = `${response.data.url}?t=${Date.now()}`;
            setMediaUrl(uploadedUrl);
            setMediaType(response.data.mediaType || mediaType);
            setMediaBlob(null); // Clear the blob since we now have the server URL
            
            setSuccess(`Header ${mediaType} saved successfully!`);
            setHasChanges(false);
            
            // Reload store config to update the Shop page
            await reloadConfig();
        } catch (err: any) {
            setError(err.response?.data?.message || `Failed to save header ${mediaType}`);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete the header media?')) {
            return;
        }

        setIsSaving(true);
        setError(null);
        setSuccess(null);

        try {
            await apiClient.delete(ApiEndpoints.AppHeader.DeleteHeader);
            
            setMediaUrl(null);
            setMediaBlob(null);
            setMediaType('image');
            setHasChanges(false);
            setSuccess('Header media deleted successfully!');
            
            // Reload store config to update the Shop page
            await reloadConfig();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete header media');
        } finally {
            setIsSaving(false);
        }
    };

    const handleObjectFitChange = async (_event: React.MouseEvent<HTMLElement>, newFit: 'cover' | 'contain' | 'fill' | null) => {
        if (!newFit) return;
        
        setObjectFit(newFit);
        setIsSaving(true);
        setError(null);

        try {
            await apiClient.patch(ApiEndpoints.AppHeader.UpdateHeaderPosition, {
                objectFit: newFit,
            });
            setSuccess('Resize mode updated!');
            await reloadConfig();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update resize mode');
            // Revert on error
            setObjectFit(config.appHeaderObjectFit || 'cover');
        } finally {
            setIsSaving(false);
        }
    };

    // Debounced save for position/scale changes
    const savePositionAndScale = useCallback(async (position: { x: number; y: number }, scale: number) => {
        setIsSaving(true);
        setError(null);

        try {
            await apiClient.patch(ApiEndpoints.AppHeader.UpdateHeaderPosition, {
                position,
                scale,
            });
            await reloadConfig();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update position/scale');
        } finally {
            setIsSaving(false);
        }
    }, [reloadConfig]);

    const handlePositionXChange = (_event: Event, value: number | number[]) => {
        const newX = value as number;
        setVideoPosition(prev => ({ ...prev, x: newX }));
    };

    const handlePositionYChange = (_event: Event, value: number | number[]) => {
        const newY = value as number;
        setVideoPosition(prev => ({ ...prev, y: newY }));
    };

    const handleScaleChange = (_event: Event, value: number | number[]) => {
        setVideoScale(value as number);
    };

    const handlePositionScaleCommit = () => {
        savePositionAndScale(videoPosition, videoScale);
    };

    const isCurrentVideo = mediaType === 'video';

    return (
        <>
            {/* Alerts */}
            {error && (
                <Alert severity='error' sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity='success' sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                {isCurrentVideo && mediaUrl && (
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: '0.7rem',
                        mr: 'auto'
                    }}>
                        <Videocam sx={{ fontSize: 14 }} />
                        Video
                    </Box>
                )}
                {mediaUrl && (
                    <PermissionWrapper screen='app-header' permission='delete'>
                        <BasicButton
                            title='Delete'
                            variant='outlined'
                            onClick={handleDelete}
                            isLoading={isSaving}
                            startIcon={<DeleteOutline fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                                borderColor: theme.palette.error.main,
                                color: theme.palette.error.main,
                                '&:hover': {
                                    borderColor: theme.palette.error.dark,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(244, 67, 54, 0.08)'
                                        : 'rgba(244, 67, 54, 0.04)'
                                }
                            }}
                        />
                    </PermissionWrapper>
                )}
                {hasChanges && (
                    <PermissionWrapper screen='app-header' permission='update'>
                        <BasicButton
                            title='Save'
                            variant='contained'
                            onClick={handleSave}
                            isLoading={isSaving}
                            startIcon={<Save fontSize='small' />}
                            sx={{
                                height: 32,
                                fontSize: '0.75rem',
                                px: 2,
                            }}
                        />
                    </PermissionWrapper>
                )}
            </Box>

                {/* Resize Mode Selector (for videos) */}
                {mediaUrl && isCurrentVideo && (
                    <Box sx={{ mb: 2 }}>
                        <Typography 
                            variant='caption' 
                            color='text.secondary' 
                            sx={{ mb: 1, display: 'block', fontFamily: 'primaryFont' }}
                        >
                            Resize Mode
                        </Typography>
                        <ToggleButtonGroup
                            value={objectFit}
                            exclusive
                            onChange={handleObjectFitChange}
                            size='small'
                            disabled={isSaving}
                            sx={{ width: '100%' }}
                        >
                            <ToggleButton 
                                value='cover' 
                                sx={{ flex: 1, textTransform: 'none', fontFamily: 'primaryFont' }}
                            >
                                <Tooltip title='Fill container, may crop edges'>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Fullscreen fontSize='small' />
                                        Cover
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton 
                                value='contain' 
                                sx={{ flex: 1, textTransform: 'none', fontFamily: 'primaryFont' }}
                            >
                                <Tooltip title='Fit entire video, may show black bars'>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <FitScreen fontSize='small' />
                                        Contain
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                            <ToggleButton 
                                value='fill' 
                                sx={{ flex: 1, textTransform: 'none', fontFamily: 'primaryFont' }}
                            >
                                <Tooltip title='Stretch to fill, may distort'>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AspectRatio fontSize='small' />
                                        Fill
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* Position & Scale Controls (for videos with cover mode) */}
                {mediaUrl && isCurrentVideo && objectFit === 'cover' && (
                    <Paper
                        elevation={0}
                        sx={{
                            mb: 2,
                            p: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CenterFocusStrong fontSize='small' color='action' />
                            <Typography variant='body2' sx={{ fontWeight: 600, fontFamily: 'primaryFont' }}>
                                Focal Point & Zoom
                            </Typography>
                        </Box>

                        <Grid container spacing={3}>
                            {/* Horizontal Position */}
                            <Grid item xs={12} sm={6}>
                                <Typography 
                                    variant='caption' 
                                    color='text.secondary' 
                                    sx={{ display: 'block', mb: 1, fontFamily: 'primaryFont' }}
                                >
                                    Horizontal Position
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant='caption' color='text.secondary'>L</Typography>
                                    <Slider
                                        value={videoPosition.x}
                                        onChange={handlePositionXChange}
                                        onChangeCommitted={handlePositionScaleCommit}
                                        min={0}
                                        max={100}
                                        disabled={isSaving}
                                        size='small'
                                        sx={{ flex: 1 }}
                                    />
                                    <Typography variant='caption' color='text.secondary'>R</Typography>
                                </Box>
                            </Grid>

                            {/* Vertical Position */}
                            <Grid item xs={12} sm={6}>
                                <Typography 
                                    variant='caption' 
                                    color='text.secondary' 
                                    sx={{ display: 'block', mb: 1, fontFamily: 'primaryFont' }}
                                >
                                    Vertical Position
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant='caption' color='text.secondary'>T</Typography>
                                    <Slider
                                        value={videoPosition.y}
                                        onChange={handlePositionYChange}
                                        onChangeCommitted={handlePositionScaleCommit}
                                        min={0}
                                        max={100}
                                        disabled={isSaving}
                                        size='small'
                                        sx={{ flex: 1 }}
                                    />
                                    <Typography variant='caption' color='text.secondary'>B</Typography>
                                </Box>
                            </Grid>

                            {/* Scale/Zoom */}
                            <Grid item xs={12}>
                                <Typography 
                                    variant='caption' 
                                    color='text.secondary' 
                                    sx={{ display: 'block', mb: 1, fontFamily: 'primaryFont' }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ZoomIn fontSize='small' />
                                        Zoom ({(videoScale * 100).toFixed(0)}%)
                                    </Box>
                                </Typography>
                                <Slider
                                    value={videoScale}
                                    onChange={handleScaleChange}
                                    onChangeCommitted={handlePositionScaleCommit}
                                    min={0.5}
                                    max={3}
                                    step={0.1}
                                    disabled={isSaving}
                                    size='small'
                                    marks={[
                                        { value: 0.5, label: '50%' },
                                        { value: 1, label: '100%' },
                                        { value: 2, label: '200%' },
                                        { value: 3, label: '300%' },
                                    ]}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Upload Section */}
                <Paper
                    elevation={0}
                    sx={{
                        border: `2px dashed ${theme.palette.divider}`,
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : 'rgba(0, 0, 0, 0.01)',
                    }}
                >
                    <CloudUpload
                        sx={{
                            fontSize: 40,
                            color: theme.palette.text.secondary,
                            mb: 1.5,
                        }}
                    />
                    <Typography
                        variant='body2'
                        sx={{
                            fontFamily: 'primaryFont',
                            fontWeight: 600,
                            mb: 0.5,
                        }}
                    >
                        {mediaUrl ? 'Change Header' : 'Upload Header'}
                    </Typography>
                    <Typography
                        variant='caption'
                        sx={{
                            color: theme.palette.text.secondary,
                            fontFamily: 'primaryFont',
                            display: 'block',
                            mb: 1,
                        }}
                    >
                        <Box component='span' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <ImageIcon sx={{ fontSize: 14 }} /> Images: 1920×600 recommended, max 5MB
                        </Box>
                    </Typography>
                    <Typography
                        variant='caption'
                        sx={{
                            color: theme.palette.text.secondary,
                            fontFamily: 'primaryFont',
                            display: 'block',
                            mb: 2,
                        }}
                    >
                        <Box component='span' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Videocam sx={{ fontSize: 14 }} /> Videos: MP4, WebM supported, max 3MB
                        </Box>
                    </Typography>
                    <input
                        accept='image/png,image/jpeg,image/jpg,image/webp,video/mp4,video/webm,video/quicktime'
                        style={{ display: 'none' }}
                        id='header-media-upload'
                        type='file'
                        onChange={handleFileSelect}
                    />
                    <label htmlFor='header-media-upload' style={{ cursor: 'pointer' }}>
                        <Button
                            variant='outlined'
                            component='span'
                            startIcon={<CloudUpload />}
                            size='small'
                        >
                            Select File
                        </Button>
                    </label>
                </Paper>

            {/* Crop Modal (only for images) */}
            <Dialog
                open={isCropModalOpen}
                onClose={handleCropCancel}
                maxWidth='md'
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                    },
                }}
            >
                <DialogTitle>
                    <Typography
                        sx={{
                            fontFamily: 'primaryFont',
                            fontWeight: 600,
                        }}
                    >
                        Crop Header Image
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            position: 'relative',
                            width: '100%',
                            height: 400,
                            backgroundColor: '#000',
                        }}
                    >
                        {imageToCrop && (
                            <Cropper
                                image={imageToCrop}
                                crop={crop}
                                zoom={zoom}
                                aspect={1 / 0.22}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>
                    <Typography
                        variant='caption'
                        sx={{
                            display: 'block',
                            mt: 2,
                            color: theme.palette.text.secondary,
                            fontFamily: 'primaryFont',
                        }}
                    >
                        Drag to reposition • Scroll to zoom • Optimized for desktop (4.5:1 ratio)
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCropCancel} color='inherit'>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCropConfirm}
                        variant='contained'
                        startIcon={<Crop />}
                    >
                        Apply Crop
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
